const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

interface RazorpayHandlerResponse {
  razorpay_subscription_id: string
  razorpay_payment_id: string
  razorpay_signature: string
}

interface RazorpayOrderHandlerResponse {
  razorpay_order_id: string
  razorpay_payment_id: string
  razorpay_signature: string
}

interface RazorpayOptions {
  key: string
  /** Recurring checkouts identify the schedule instead of a single order. */
  subscription_id: string
  name: string
  description: string
  prefill: { name: string; email: string }
  theme: { color: string }
  handler: (response: RazorpayHandlerResponse) => void
  modal: { ondismiss: () => void }
}

interface RazorpayOrderOptions {
  key: string
  /** One-time purchases (e.g. credit top-ups) identify a specific order instead of a schedule. */
  order_id: string
  amount: number
  currency: string
  name: string
  description: string
  prefill: { name: string; email: string }
  theme: { color: string }
  handler: (response: RazorpayOrderHandlerResponse) => void
  modal: { ondismiss: () => void }
}

declare global {
  interface Window {
    Razorpay?: {
      new (options: RazorpayOptions): { open: () => void }
      new (options: RazorpayOrderOptions): { open: () => void }
    }
  }
}

let loader: Promise<void> | null = null

/** Loads checkout.js once and reuses the same promise for every later call. */
function loadScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve()
  if (loader) return loader

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      // Cleared so a later attempt can retry rather than resolving against a dead promise.
      loader = null
      reject(new Error('Could not reach Razorpay. Check your connection and try again.'))
    }
    document.head.appendChild(script)
  })
  return loader
}

export interface RazorpayCheckoutInput {
  keyId: string
  subscriptionId: string
  name: string
  description: string
  prefill: { name: string; email: string }
}

/**
 * Opens the Razorpay modal and resolves with the handshake fields, which the server
 * re-verifies. Resolves null when the payer closes the modal without paying.
 */
export async function openRazorpayCheckout(
  input: RazorpayCheckoutInput,
): Promise<RazorpayHandlerResponse | null> {
  await loadScript()
  const Razorpay = window.Razorpay
  if (!Razorpay) throw new Error('Razorpay checkout is unavailable right now.')

  const accent =
    getComputedStyle(document.documentElement).getPropertyValue('--md-primary').trim() || '#006a63'

  return new Promise((resolve) => {
    let settled = false
    const instance = new Razorpay({
      key: input.keyId,
      // Razorpay reads the amount and currency from the subscription's plan, not from here.
      subscription_id: input.subscriptionId,
      name: input.name,
      description: input.description,
      prefill: input.prefill,
      theme: { color: accent },
      handler: (response) => {
        settled = true
        resolve(response)
      },
      modal: {
        ondismiss: () => {
          if (!settled) resolve(null)
        },
      },
    })
    instance.open()
  })
}

export interface RazorpayOrderCheckoutInput {
  keyId: string
  orderId: string
  amountMinor: number
  currency: string
  name: string
  description: string
  prefill: { name: string; email: string }
}

/** Same modal as {@link openRazorpayCheckout}, but binds to a one-time order instead of a subscription. */
export async function openRazorpayOrderCheckout(
  input: RazorpayOrderCheckoutInput,
): Promise<RazorpayOrderHandlerResponse | null> {
  await loadScript()
  const Razorpay = window.Razorpay
  if (!Razorpay) throw new Error('Razorpay checkout is unavailable right now.')

  const accent =
    getComputedStyle(document.documentElement).getPropertyValue('--md-primary').trim() || '#006a63'

  return new Promise((resolve) => {
    let settled = false
    const instance = new Razorpay({
      key: input.keyId,
      order_id: input.orderId,
      amount: input.amountMinor,
      currency: input.currency,
      name: input.name,
      description: input.description,
      prefill: input.prefill,
      theme: { color: accent },
      handler: (response) => {
        settled = true
        resolve(response)
      },
      modal: {
        ondismiss: () => {
          if (!settled) resolve(null)
        },
      },
    })
    instance.open()
  })
}
